import { gql } from "@apollo/client";

import { CustomizeField } from "./_fragments";

/**
 * Dedicated GraphQL documents for the Support Ticket module.
 *
 * Kept in its own file (rather than the shared `_queries`/`_mutations`/
 * `_fragments` blobs) so the Support Ticket surface can evolve independently of
 * Work Orders. Re-exported through `~/api`.
 */

export const SupportTicket = {
  fragments: {
    // Card-level fields used by the list / table / board / map views.
    SupportTicketCard: gql`
      fragment SupportTicketCard on SupportTicket {
        _id
        supportTicketId
        title
        status
        type {
          _id
          name
          icon
          color
          isInternal
          isSystem
        }
        company {
          _id
          name
          # External connection id, rendered as "Name · ID" in the activity log.
          # facilityIdentifier is the identifier on Customer — there is no
          # facilityId on this type.
          facilityIdentifier
          isDeleted
        }
        assets {
          _id
          name
          serialNumber
          isDeleted
          image
          thumbnail
          oem {
            _id
            logo
          }
        }
        assignees {
          _id
          name
        }
        teams {
          _id
          name
          teamColor
        }
        createdBy {
          _id
          name
        }
        isCreatorInternal
        isMyTicket
        lastUpdatedAt
        createdAt
        # Computed field resolvers (synchronous string builders over
        # _id/oem/company) — no per-field cost at list scale, so the card views
        # can read unread counts straight off the channel ids. Company-less
        # tickets return an empty external array and one internal channel.
        # NOTE: PortalSupportTicket has no internal-notes field — never share
        # this fragment with the customer-portal queries.
        supportTicketChatChannels
        supportTicketInternalNotesChatChannels
      }
    `,
  },
};

SupportTicket.fragments.SupportTicketFull = gql`
  fragment SupportTicketFull on SupportTicket {
    ...SupportTicketCard
    description
    teams {
      _id
      members {
        _id
        name
      }
    }
    createdBy {
      _id
      name
      email
    }
    createdByContact {
      _id
      name
      email
    }
    numberOfTeams
    inventoryParts {
      part {
        _id
        name
        articleNumber
        availableStock
        visibilityScope
        image
        thumbnail
      }
      addedBy {
        _id
        name
      }
      quantity
      assetId
      asset {
        _id
        name
        serialNumber
      }
      showOnPortal
    }
    linkedWorkOrders {
      ticket {
        _id
        title
        ticketId
        status
        ticketType {
          icon
          color
          isInternal
        }
      }
      linkedBy {
        _id
        name
      }
      linkedAt
    }
    customFields {
      fieldId {
        ...CustomAdditionFieldData
      }
      value
      values
    }
    unaffiliatedAssetLinkedTo {
      kind
      documentId
    }
    # Escalation. A portal that cannot resolve a ticket hands it to the OEM,
    # which is why the ticket is on every OEM role's list. De-escalation hands
    # it back down to a portal of the OEM's choosing.
    escalatedToOem
    # Every company that has held the ticket, oldest first — a trail, not a
    # pointer at the current holder, and NOT cleared on escalation (everyone on
    # it keeps seeing the ticket and keeps their place in its chat). A company
    # handed it twice moves to the end rather than appearing twice.
    # The current holder is derived: escalatedToOem wins, else the last entry
    # here, else the reroute destination (no field for that on this type yet).
    # Detail-only: the list/kanban views have no use for it.
    handedTo {
      _id
      name
    }
    # The connection that escalated it, while it is escalated. Cleared on
    # hand-down, so its presence is what decides whether the row is shown.
    escalatedBy {
      _id
      name
    }
    updatedAt
  }
  ${SupportTicket.fragments.SupportTicketCard}
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;

/* -------------------------------- Queries -------------------------------- */

export const LIST_OWN_OEM_SUPPORT_TICKETS_QUERY = gql`
  query listOwnOemSupportTickets($params: InputQueryParams) {
    listOwnOemSupportTickets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      supportTickets {
        ...SupportTicketCard
        # Values only — the table view resolves labels/options/type from the
        # OEM-level definitions (listOwnOemCustomFields, type "supportTickets").
        # Kept out of SupportTicketCard so the kanban query stays lean.
        customFields {
          fieldId {
            _id
          }
          value
          values
        }
      }
    }
  }
  ${SupportTicket.fragments.SupportTicketCard}
`;

export const LIST_OWN_OEM_KANBAN_SUPPORT_TICKETS_QUERY = gql`
  query listOwnOemKanbanSupportTickets($params: InputQueryParams) {
    listOwnOemKanbanSupportTickets(params: $params) {
      totalCount
      columns {
        _id
        boardFor
        type
        # Id of the single-select custom field this column groups by. The board
        # matches it against the field list to resolve the column's colour.
        customField
        column
        columnIndex
        totalCount
        limit
        skip
        currentPage
        status {
          _id
          label
          color
        }
        cards {
          ...SupportTicketCard
        }
      }
    }
  }
  ${SupportTicket.fragments.SupportTicketCard}
`;

export const TOTAL_SUPPORT_TICKETS_QUERY = gql`
  query totalSupportTickets($params: InputQueryParams) {
    totalSupportTickets(params: $params)
  }
`;

/**
 * Whether any support ticket still carries a given status. Unlike the work-order
 * counterpart (`checkTicketHasStatus`) this is not scoped to the caller's ticket
 * visibility, so it cannot under-count for users with narrow team access.
 */
export const CHECK_SUPPORT_TICKET_HAS_STATUS_QUERY = gql`
  query checkSupportTicketHasStatus($statusId: ID!) {
    checkSupportTicketHasStatus(statusId: $statusId)
  }
`;

export const LIST_EMAIL_THREADS_BY_SUPPORT_TICKET_QUERY = gql`
  query listEmailThreadsBySupportTicket(
    $scope: String!
    $supportTicketId: ID!
    $emailAddressId: ID!
  ) {
    listEmailThreadsBySupportTicket(
      scope: $scope
      supportTicketId: $supportTicketId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      participants {
        name
        email
      }
    }
  }
`;

export const LIST_SUPPORT_TICKET_ASSIGNABLE_USERS_QUERY = gql`
  query listSupportTicketAssignableUsers($supportTicketId: ID!) {
    listSupportTicketAssignableUsers(supportTicketId: $supportTicketId) {
      _id
      name
      email
      role
      teams {
        _id
        name
        teamColor
        members {
          _id
          name
        }
      }
    }
  }
`;

export const LIST_OWN_SUPPORT_TICKET_MENTIONABLE_USERS_QUERY = gql`
  query listOwnSupportTicketMentionableUsers($supportTicketId: ID!) {
    listOwnSupportTicketMentionableUsers(supportTicketId: $supportTicketId) {
      oemUsers {
        _id
        name
        teams {
          _id
          name
          teamColor
        }
      }
      facilityUsers {
        _id
        name
        contactId
        contactName
        teams {
          _id
          name
          teamColor
        }
      }
    }
  }
`;

/**
 * Companies an escalated ticket may be handed back down to.
 *
 * Server-computed and not reproducible client-side: it is the creator's
 * ancestors that still hold one of the ticket's assets, which needs both the
 * company hierarchy and the asset's share chain. Never build this list locally.
 *
 * Legitimately empty — a root portal's own ticket has no ancestors — so callers
 * must say so rather than render an empty picker.
 */
export const LIST_SUPPORT_TICKET_DE_ESCALATION_TARGETS_QUERY = gql`
  query listSupportTicketDeEscalationTargets(
    $input: InputSupportTicketDeEscalationTargets!
  ) {
    listSupportTicketDeEscalationTargets(input: $input) {
      _id
      name
    }
  }
`;

export const GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY = gql`
  query getOwnOemSupportTicketById($id: ID!) {
    getOwnOemSupportTicketById(id: $id) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

/* ------------------------------- Mutations ------------------------------- */

export const CREATE_OWN_OEM_SUPPORT_TICKET_MUTATION = gql`
  mutation createOwnOemSupportTicket($input: InputCreateOwnOemSupportTicket!) {
    createOwnOemSupportTicket(input: $input) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

export const UPDATE_OWN_OEM_SUPPORT_TICKET_MUTATION = gql`
  mutation updateOwnOemSupportTicket($input: InputUpdateOwnOemSupportTicket!) {
    updateOwnOemSupportTicket(input: $input) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

/**
 * Atomic type change: swaps the ticket's type and reconciles the fields that
 * type drives (title, parts, custom fields). Separate from the generic update
 * so the server can validate the target type's required fields and the
 * internal/external constraint in one transaction.
 *
 * Note the input field is `type` (the generic update's name), not the work
 * order mutation's `ticketType`.
 */
export const UPDATE_OWN_OEM_SUPPORT_TICKET_TYPE_MUTATION = gql`
  mutation updateOwnOemSupportTicketType(
    $input: InputUpdateOwnOemSupportTicketType!
  ) {
    updateOwnOemSupportTicketType(input: $input) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

/**
 * Hand an escalated ticket back down to a portal.
 *
 * `@hasRole(["OEM"])` — Owners and Admins only; Supervisors and Technicians are
 * refused, so the control is hidden from them.
 *
 * Asymmetric with the portal's escalate on purpose: a portal may only ever
 * escalate to the OEM, while the OEM may place a ticket with any portal on the
 * chain — `connectionId` need not be the portal that escalated it. On success
 * the ticket leaves every OEM list, including the caller's own.
 */
export const DE_ESCALATE_SUPPORT_TICKET_MUTATION = gql`
  mutation deEscalateSupportTicket($input: InputDeEscalateSupportTicket!) {
    deEscalateSupportTicket(input: $input) {
      _id
      escalatedToOem
      handedTo {
        _id
        name
      }
      # Cleared by the hand-down; returned so the sidebar row disappears with it.
      escalatedBy {
        _id
        name
      }
    }
  }
`;

export const DELETE_OWN_OEM_SUPPORT_TICKET_MUTATION = gql`
  mutation deleteOwnOemSupportTicket($input: InputDeleteOwnOemSupportTicket!) {
    deleteOwnOemSupportTicket(input: $input)
  }
`;

export const BULK_DELETE_OWN_OEM_SUPPORT_TICKETS_MUTATION = gql`
  mutation bulkDeleteOwnOemSupportTickets($ids: [ID!]!, $reason: String) {
    bulkDeleteOwnOemSupportTickets(ids: $ids, reason: $reason) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const ASSIGN_INVENTORY_PARTS_TO_SUPPORT_TICKET_MUTATION = gql`
  mutation assignInventoryPartsToSupportTicket(
    $input: InputAssignInventoryPartsToSupportTicket!
  ) {
    assignInventoryPartsToSupportTicket(input: $input) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

export const REMOVE_INVENTORY_PART_FROM_SUPPORT_TICKET_MUTATION = gql`
  mutation removeInventoryPartFromSupportTicket(
    $input: InputRemoveInventoryPartFromSupportTicket!
  ) {
    removeInventoryPartFromSupportTicket(input: $input) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

export const EDIT_INVENTORY_PART_ON_SUPPORT_TICKET_MUTATION = gql`
  mutation editInventoryPartOnSupportTicket(
    $input: InputEditInventoryPartOnSupportTicket!
  ) {
    editInventoryPartOnSupportTicket(input: $input) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

export const LINK_SUPPORT_TICKET_TO_WORK_ORDER_MUTATION = gql`
  mutation linkSupportTicketToWorkOrder(
    $supportTicketId: ID!
    $workOrderId: ID!
  ) {
    linkSupportTicketToWorkOrder(
      supportTicketId: $supportTicketId
      workOrderId: $workOrderId
    ) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;

export const UNLINK_SUPPORT_TICKET_FROM_WORK_ORDER_MUTATION = gql`
  mutation unlinkSupportTicketFromWorkOrder(
    $supportTicketId: ID!
    $workOrderId: ID!
  ) {
    unlinkSupportTicketFromWorkOrder(
      supportTicketId: $supportTicketId
      workOrderId: $workOrderId
    ) {
      ...SupportTicketFull
    }
  }
  ${SupportTicket.fragments.SupportTicketFull}
`;
